(() => {

  /* HOK+ (2026-09-12): membership line to add alongside the bundle so the
     drawer renders member pricing in the same round trip. Mirrors
     hokMembershipItem() in the bundle ATC snippets. */
  function hokMembershipLineItem() {
    try {
      if (sessionStorage.getItem('hok_plus_opt_out')) return null;
      if (/(?:^|;\s*)hok_member=1(?:;|$)/.test(document.cookie)) return null;
      const hok = window.HokMembership;
      if (hok && typeof hok.status === 'function' && hok.status().lastMembershipQty > 0) return null;
      const cfg = hok ? hok.config : JSON.parse(document.getElementById('HokMembershipConfig').textContent);
      if (!cfg || !cfg.enabled || !cfg.productValid || cfg.optedOut || cfg.suppressed) return null;
      return { id: parseInt(cfg.variantId, 10), quantity: 1, selling_plan: parseInt(cfg.sellingPlanId, 10), properties: { _hok_auto_added: 'true' } };
    } catch (e) { return null; }
  }
  /* Show the drawer through the HOK+ controller when present (it validates the
     section HTML and keeps enrollment in sync), else via the theme drawer. */
  function hokShowDrawer(cartDrawer, result) {
    const hok = window.HokMembership;
    const open = () => { try { cartDrawer.open?.(); } catch (e) { /* noop */ } };
    if (hok && typeof hok.ingestSections === 'function' && result && result.sections) {
      return Promise.resolve(hok.ingestSections(result.sections)).then(open, open);
    }
    try { cartDrawer.renderContents(result); } catch (drawerError) { open(); }
    return Promise.resolve();
  }

  class HokPdp extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      this.quantity = 3;
      this.discount = 33.333333;
      this.discountCode = '';
      this.subscriptionDays = 60;
      this.purchase = this.dataset.defaultPurchase === 'once' ? 'once' : 'subscribe';
      this.cartRedirect = this.dataset.redirect === 'cart' ? 'cart' : 'checkout';
      // Mirror of the automatic HOK+ cart discount, shown in one-time prices only.
      this.memberPct = Math.min(90, Math.max(0, Number(this.dataset.memberDiscount || 0)));
      this.fixedPrice = 0; // one-time bundle total in cents when the offer carries a fixed price
      this.selected = new Map();
      this.rows = [...this.querySelectorAll('[data-scent-row]')];
      this.offerCards = [...this.querySelectorAll('[data-offer-card]')];
      this.activeOffer = this.offerCards.find((card) => card.classList.contains('is-active')) || this.offerCards[0];
      this.bindEvents();
      this.selectOffer(this.activeOffer, false);
      this.showGallerySlide(0);
      this.bindScrollProgress('[data-ingredient-track]', '[data-ingredient-progress]');
      this.bindScrollProgress('[data-how-to-track]', '[data-how-to-progress]');
      this.bindScrollProgress('[data-quote-track]', '[data-quote-progress]');
      this.syncKachingTiers();
    }

    /* Live Kaching tier prices (2026-09-19): same mechanism as the breath lander —
       when a deal ID is configured, one-time bundle totals are read from the Kaching
       app embed so price edits in Kaching show here without a theme change. The
       offer fixed-total settings stay as the fallback. */
    syncKachingTiers(attempt = 0) {
      const dealId = this.dataset.kachingDealId;
      if (!dealId) return;
      const api = window.kachingBundlesApi;
      if (!api || typeof api.fetchDeals !== 'function') {
        if (attempt < 20) setTimeout(() => this.syncKachingTiers(attempt + 1), 500);
        return;
      }
      Promise.resolve(api.fetchDeals()).then((deals) => {
        const deal = (Array.isArray(deals) ? deals : []).find((entry) => entry.id === dealId || entry.nanoId === dealId);
        if (!deal) return;
        // Deal prices are in the store currency; scale for other currencies.
        const storeCents = Math.round(Number(this.dataset.singleStorePrice || 0) * 100);
        const factor = storeCents > 0 ? this.getMainVariant().price / storeCents : 1;
        let changed = false;
        (deal.dealBars || []).forEach((bar) => {
          let totalQuantity = 0;
          let value = 0;
          if (bar.dealBarType === 'quantity-break' && bar.discount?.type === 'specific') {
            totalQuantity = Number(bar.quantity);
            value = Number(bar.discount.value);
          } else if (bar.dealBarType === 'bxgy' && bar.buyDiscount?.type === 'specific' && Number(bar.getDiscount?.value) === 100) {
            // "Buy X get Y free" bar: the buy total is the whole bundle price.
            totalQuantity = Number(bar.buyQuantity) + Number(bar.getQuantity);
            value = Number(bar.buyDiscount.value);
          } else return;
          const card = this.offerCards.find((item) => Number(item.dataset.quantity) === totalQuantity);
          const cents = Math.round(value * 100 * factor);
          if (!card || !(cents > 0) || Number(card.dataset.fixedPrice) === cents) return;
          card.dataset.fixedPrice = String(cents);
          changed = true;
        });
        if (!changed) return;
        this.fixedPrice = Math.max(0, Number(this.activeOffer?.dataset.fixedPrice || 0));
        this.sync();
      }).catch(() => {});
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const offer = event.target.closest('[data-offer-card]');
        if (offer) return this.selectOffer(offer, true);

        const add = event.target.closest('[data-add-scent]');
        if (add) return this.changeScent(add.closest('[data-scent-row]'), 1);
        const remove = event.target.closest('[data-remove-scent]');
        if (remove) return this.changeScent(remove.closest('[data-scent-row]'), -1);
        const increase = event.target.closest('[data-increase-scent]');
        if (increase) return this.changeScent(increase.closest('[data-scent-row]'), 1);

        const purchase = event.target.closest('[data-purchase]');
        if (purchase) return this.selectPurchase(purchase.dataset.purchase);

        if (event.target.closest('[data-add-to-cart]') || event.target.closest('[data-sticky-cart]')) return this.addToCart();
        if (event.target.closest('[data-gallery-previous]')) return this.moveGallery(-1);
        if (event.target.closest('[data-gallery-next]')) return this.moveGallery(1);

        const thumb = event.target.closest('[data-gallery-thumb]');
        if (thumb) return this.showGallerySlide(Number(thumb.dataset.galleryThumb));

        if (event.target.closest('[data-scroll-scents]')) {
          this.querySelector('[data-scent-list]')?.scrollBy({ top: 150, behavior: 'smooth' });
          return;
        }
        if (event.target.closest('[data-ingredients-previous]')) return this.scrollIngredients(-1);
        if (event.target.closest('[data-ingredients-next]')) return this.scrollIngredients(1);
        if (event.target.closest('[data-scroll-to-shop]')) return this.querySelector('#shop')?.scrollIntoView({ behavior: 'smooth' });
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-main-variant]')) this.updatePricing();
        if (event.target.matches('[data-scent-variant]')) this.sync();
      });
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format((Number(cents) || 0) / 100);
      } catch (_) {
        return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
      }
    }

    getMainVariant() {
      const field = this.querySelector('[data-main-variant]');
      if (!field) return { price: 0, compare: 0 };
      const source = field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
      return { price: Number(source?.dataset.price || field.dataset.price || 0), compare: Number(source?.dataset.compare || field.dataset.compare || 0) };
    }

    getSellingPlan(row, variantId, days = this.subscriptionDays) {
      const allocations = [...row.querySelectorAll(`[data-loop-allocation][data-variant-id="${variantId}"]`)];
      const patterns = {
        30: [/(^|\D)30\s*days?(\D|$)/i, /(^|\D)1\s*months?(\D|$)/i, /\bmonthly\b/i],
        60: [/(^|\D)60\s*days?(\D|$)/i, /(^|\D)2\s*months?(\D|$)/i],
        90: [/(^|\D)90\s*days?(\D|$)/i, /(^|\D)3\s*months?(\D|$)/i, /\bquarterly\b/i]
      };
      const matchers = patterns[Number(days)] || [new RegExp(`(^|\\D)${Number(days)}\\s*days?(\\D|$)`, 'i')];
      const allocation = allocations.find((item) => matchers.some((pattern) => pattern.test(item.dataset.sellingPlanLabel || '')));
      if (!allocation) return null;
      return {
        id: allocation.dataset.sellingPlan || '',
        price: Number(allocation.dataset.sellingPrice || 0),
        label: allocation.dataset.sellingPlanLabel || ''
      };
    }

    getRowVariant(row, days = this.subscriptionDays) {
      const field = row.querySelector('[data-scent-variant]');
      if (!field) return null;
      const source = field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
      const id = Number(source?.value || field.value);
      const price = Number(source?.dataset.price || field.dataset.price || 0);
      const plan = this.getSellingPlan(row, id, days);
      return {
        id,
        price,
        sellingPlan: plan?.id || '',
        sellingPrice: plan?.price || price,
        sellingPlanLabel: plan?.label || ''
      };
    }

    selectOffer(card, resetSelection = true) {
      if (!card) return;
      this.activeOffer = card;
      this.quantity = Number(card.dataset.quantity || 1);
      this.discount = Number(card.dataset.discount || 0);
      this.discountCode = card.dataset.discountCode || '';
      this.fixedPrice = Math.max(0, Number(card.dataset.fixedPrice || 0));
      this.subscriptionDays = Number(card.dataset.subscriptionDays || 30);
      this.offerCards.forEach((item) => item.classList.toggle('is-active', item === card));

      if (resetSelection) this.selected.clear();
      this.sync();
    }

    selectPurchase(value) {
      this.purchase = value === 'subscribe' ? 'subscribe' : 'once';
      this.querySelectorAll('[data-purchase]').forEach((button) => button.classList.toggle('is-active', button.dataset.purchase === this.purchase));
      this.sync();
    }

    changeScent(row, change) {
      if (!row) return;
      const total = this.selectedTotal();
      const current = this.selected.get(row) || 0;
      if (change > 0 && total >= this.quantity) return;
      if (change < 0 && current <= 0) return;
      const next = Math.max(0, current + change);
      if (next) this.selected.set(row, next);
      else this.selected.delete(row);
      this.sync();
    }

    selectedTotal() {
      return [...this.selected.values()].reduce((sum, count) => sum + count, 0);
    }

    selectedSubtotal() {
      let subtotal = 0;
      this.selected.forEach((count, row) => {
        const variant = this.getRowVariant(row);
        if (!variant) return;
        subtotal += (this.purchase === 'subscribe' ? variant.sellingPrice : variant.price) * count;
      });
      return subtotal;
    }

    memberPrice(cents) {
      // Shopify rounds the automatic HOK+ discount amount DOWN when allocating
      // (verified 2026-09-19), so subtract the floored discount.
      return cents - Math.floor(cents * this.memberPct / 100);
    }

    priceAfterOffer(subtotal, discount = this.discount, fixed = this.fixedPrice) {
      // Loop selling-plan prices are already the final subscription prices.
      // Bundle percentages / fixed totals are only used for one-time purchases.
      if (this.purchase === 'subscribe') return Math.round(subtotal);
      const base = fixed > 0 ? fixed : Math.round(subtotal * Math.max(0, 1 - discount / 100));
      return this.memberPrice(base);
    }

    priceForDisplay(cents) {
      const amount = Math.round(Number(cents) || 0);
      return this.purchase === 'subscribe' ? Math.round(amount / 100) * 100 : amount;
    }

    updatePricing() {
      const main = this.getMainVariant();
      this.offerCards.forEach((card) => {
        const quantity = Number(card.dataset.quantity || 1);
        const discount = Number(card.dataset.discount || 0);
        const days = Number(card.dataset.subscriptionDays || 30);
        const exampleVariant = this.rows.length ? this.getRowVariant(this.rows[0], days) : null;
        const unitPrice = this.purchase === 'subscribe' && exampleVariant?.sellingPlan ? exampleVariant.sellingPrice : main.price;
        const cardFixed = Math.max(0, Number(card.dataset.fixedPrice || 0));
        const total = this.purchase === 'subscribe'
          ? Math.round(unitPrice * quantity)
          : this.memberPrice(cardFixed > 0 ? cardFixed : Math.round(unitPrice * quantity * (1 - discount / 100)));
        const displayTotal = this.priceForDisplay(total);
        card.querySelector('[data-offer-total]').textContent = this.money(displayTotal);
        card.querySelector('[data-offer-unit]').textContent = `${this.money(Math.round(displayTotal / quantity))} per jar`;
      });

      const currentPrice = this.querySelector('[data-main-price]');
      const comparePrice = this.querySelector('[data-main-compare]');
      const saving = this.querySelector('[data-main-saving]');
      if (currentPrice) currentPrice.textContent = this.money(main.price);
      if (comparePrice) {
        comparePrice.textContent = this.money(main.compare);
        comparePrice.hidden = !(main.compare > main.price);
      }
      if (saving) {
        const percent = main.compare > main.price ? Math.round(((main.compare - main.price) / main.compare) * 100) : 0;
        saving.textContent = percent ? `Save ${percent}%` : '';
        saving.hidden = !percent;
      }
    }

    sync() {
      const total = this.selectedTotal();
      const remaining = Math.max(0, this.quantity - total);
      this.rows.forEach((row) => {
        const count = this.selected.get(row) || 0;
        const add = row.querySelector('[data-add-scent]');
        const stepper = row.querySelector('[data-stepper]');
        const countLabel = row.querySelector('[data-scent-count]');
        const increase = row.querySelector('[data-increase-scent]');
        if (add) {
          add.hidden = count > 0;
          if (!add.dataset.soldOut) add.disabled = count === 0 && total >= this.quantity;
        }
        if (stepper) stepper.hidden = count === 0;
        if (countLabel) countLabel.textContent = count;
        if (increase) increase.disabled = total >= this.quantity;
      });

      const heading = this.querySelector('[data-selector-heading]');
      const selectionCount = this.querySelector('[data-selection-count]');
      const progress = this.querySelector('[data-progress]');
      if (heading) heading.textContent = `Choose your ${this.quantity} scent${this.quantity === 1 ? '' : 's'}`;
      if (selectionCount) selectionCount.textContent = `${total} of ${this.quantity} selected`;
      if (progress) progress.style.width = `${Math.min(100, total / this.quantity * 100)}%`;

      const frequency = this.querySelector('[data-subscription-frequency]');
      if (frequency) frequency.textContent = `Every ${this.subscriptionDays} days · Skip or cancel anytime`;
      this.querySelectorAll('[data-subscription-days-label]').forEach((label) => {
        label.textContent = `${this.subscriptionDays} days`;
      });
      this.querySelectorAll('[data-purchase-details]').forEach((panel) => {
        panel.hidden = panel.dataset.purchaseDetails !== this.purchase;
      });

      const subtotal = this.selectedSubtotal() || this.getMainVariant().price * this.quantity;
      const finalTotal = this.priceAfterOffer(subtotal);
      const displayFinalTotal = this.priceForDisplay(finalTotal);
      const completeLabel = this.cartRedirect === 'cart' ? 'Add to cart' : 'Checkout';
      const buttonText = remaining ? `Choose ${remaining} more scent${remaining === 1 ? '' : 's'}` : completeLabel; // label only (Jai 2026-09-13)
      const stickyText = remaining ? `${remaining} left` : completeLabel;
      this.querySelectorAll('[data-add-to-cart]').forEach((button) => { button.disabled = remaining > 0 || this.rows.length === 0; button.textContent = buttonText; });
      const sticky = this.querySelector('[data-sticky-cart]');
      if (sticky) { sticky.disabled = remaining > 0 || this.rows.length === 0; sticky.textContent = stickyText; }
      const stickySummary = this.querySelector('[data-sticky-summary]');
      if (stickySummary) stickySummary.textContent = `${total} of ${this.quantity} scents · ${this.money(displayFinalTotal)}`;
      this.updatePricing();
    }

    async addToCart() {
      if (this.selectedTotal() !== this.quantity) return;
      this.setError('');
      const itemsByKey = new Map();

      for (const [row, quantity] of this.selected.entries()) {
        const variant = this.getRowVariant(row);
        if (!variant?.id) return this.setError('One of the selected products does not have an available variant.');
        if (this.purchase === 'subscribe' && !variant.sellingPlan) {
          const productName = row.querySelector('h3')?.textContent?.trim() || 'One selected product';
          return this.setError(`${productName} is missing the ${this.subscriptionDays}-day Loop selling plan. Map it to that frequency in Loop, or choose one-time purchase.`);
        }
        const key = `${variant.id}:${this.purchase === 'subscribe' ? variant.sellingPlan : 'once'}`;
        const existing = itemsByKey.get(key);
        if (existing) existing.quantity += quantity;
        else {
          const item = { id: variant.id, quantity, properties: { '_HOK offer': this.activeOffer.querySelector('strong')?.textContent?.trim() || 'Bundle' } };
          if (this.purchase === 'subscribe') item.selling_plan = Number(variant.sellingPlan);
          itemsByKey.set(key, item);
        }
      }

      const buttons = [...this.querySelectorAll('[data-add-to-cart], [data-sticky-cart]')];
      buttons.forEach((button) => { button.disabled = true; button.dataset.originalText = button.textContent; button.textContent = 'Adding…'; });
      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const checkoutDiscountCode = this.purchase === 'once' ? this.discountCode : '';
        // HOK+ enrollment rides along as a line item either way, so 'checkout'
        // (2026-09-19) skips the slide cart; 'cart' keeps the 2026-09-12 drawer
        // flow. A bundle discount code is applied through the /discount cookie.
        const useDrawer = this.cartRedirect !== 'checkout' && !!(cartDrawer && typeof cartDrawer.renderContents === 'function');
        const payload = { items: [...itemsByKey.values()] };
        const hokItem = hokMembershipLineItem();
        if (hokItem) payload.items.push(hokItem);
        if (useDrawer) {
          payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
          payload.sections_url = '/'; // rendering under the product URL returns a broken drawer
        }
        if (checkoutDiscountCode) {
          try { await fetch(`/discount/${encodeURIComponent(checkoutDiscountCode)}`, { credentials: 'same-origin' }); } catch (discountError) { /* still applied by the fallback redirect */ }
        }
        const response = await fetch(this.dataset.cartAddUrl || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'The selected products could not be added.');

        if (useDrawer) {
          await hokShowDrawer(cartDrawer, result);
          return;
        }
        const destinationUrl = `${window.Shopify?.routes?.root || '/'}${this.cartRedirect === 'checkout' ? 'checkout' : 'cart'}`;
        window.location.href = checkoutDiscountCode
          ? `/discount/${encodeURIComponent(checkoutDiscountCode)}?redirect=${encodeURIComponent(destinationUrl)}`
          : destinationUrl;
        return;
      } catch (error) {
        this.setError(error.message || 'Something went wrong. Please try again.');
      } finally {
        buttons.forEach((button) => { button.textContent = button.dataset.originalText || button.textContent; });
        this.sync();
      }
    }

    setError(message) {
      const error = this.querySelector('[data-form-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }

    showGallerySlide(index) {
      const slides = [...this.querySelectorAll('[data-gallery-slide]')];
      if (!slides.length) return;
      this.galleryIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, position) => { slide.hidden = position !== this.galleryIndex; slide.classList.toggle('is-active', position === this.galleryIndex); });
      this.querySelectorAll('[data-gallery-thumb]').forEach((thumb, position) => thumb.classList.toggle('is-active', position === this.galleryIndex));
    }

    moveGallery(direction) {
      this.showGallerySlide((this.galleryIndex || 0) + direction);
    }

    scrollIngredients(direction) {
      const track = this.querySelector('[data-ingredient-track]');
      if (!track) return;
      track.scrollBy({ left: direction * Math.min(420, window.innerWidth * .82), behavior: 'smooth' });
    }

    bindScrollProgress(trackSelector, progressSelector) {
      const track = this.querySelector(trackSelector);
      const progress = this.querySelector(progressSelector);
      if (!track || !progress) return;

      let frame;
      const update = () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          const cards = [...track.children].filter((child) => child.nodeType === 1);
          const count = Math.max(1, cards.length);
          const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
          const ratio = maxScroll ? Math.min(1, Math.max(0, track.scrollLeft / maxScroll)) : 0;
          const width = 100 / count;
          progress.style.width = `${width}%`;
          progress.style.left = `${ratio * (100 - width)}%`;
        });
      };

      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      update();
    }
  }

  if (!customElements.get('hok-pdp')) customElements.define('hok-pdp', HokPdp);
})();

/* cache-bust 2026-09-29 (Session B: rotate asset URLs after iPhone Safari unstyled-page report) */
